"use client";

import { memo, type ComponentType, type SVGProps } from "react";
import SafeImage from "@/components/shared/SafeImage";
import * as TablerIcons from "@tabler/icons-react";
import * as IconoirIcons from "iconoir-react";
import type { HotelDetailAttribute } from "@/types/api";
import { cn } from "@/lib/utils";

type DynamicIconProps = Omit<SVGProps<SVGSVGElement>, "stroke"> & {
  stroke?: number | string;
};

type DynamicIconComponent = ComponentType<DynamicIconProps>;

const tabler = TablerIcons as unknown as Record<string, DynamicIconComponent>;
const iconoir = IconoirIcons as unknown as Record<string, DynamicIconComponent>;
const iconCache = new Map<string, DynamicIconComponent>();

const getIconComponent = (name = "", type = ""): DynamicIconComponent => {
  const cacheKey = `${type}:${name}`;
  const cached = iconCache.get(cacheKey);
  if (cached) return cached;

  const clean = name.trim();
  const pascal = clean
    .replace(/[-_ ]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
    .replace(/^[a-z]/, (c) => c.toUpperCase());
  const withIcon = pascal.startsWith("Icon") ? pascal : `Icon${pascal}`;
  const withoutIcon = pascal.replace(/^Icon/, "");

  const keys = [clean, withIcon, pascal, withoutIcon];
  const maps = type === "iconoir" ? [iconoir, tabler] : [tabler, iconoir];

  let resolved: DynamicIconComponent = TablerIcons.IconPlant2;
  for (const key of keys) {
    if (!key) continue;
    for (const m of maps) {
      if (m[key]) {
        resolved = m[key];
        break;
      }
    }
    if (resolved !== TablerIcons.IconPlant2) break;
  }

  iconCache.set(cacheKey, resolved);
  return resolved;
};

export const AttributeIcon = memo(
  ({
    attribute,
    className,
  }: {
    attribute?: HotelDetailAttribute;
    className?: string;
  }) => {
    if (!attribute) return null;

    const isFile =
      attribute.type === "file" ||
      /^https?:\/\/|^\/|\.(svg|png|jpe?g|webp)($|\?)/i.test(
        attribute.iconName || "",
      );

    if (isFile) {
      return attribute.iconName ? (
        <SafeImage
          src={attribute.iconName}
          alt={attribute.name || "icon"}
          width={22}
          height={22}
          unoptimized
          className={cn("size-[22px] object-contain", className)}
        />
      ) : (
        <TablerIcons.IconPlant2
          className={cn("size-[22px]", className)}
          stroke={1.8}
        />
      );
    }

    const IconComponent = getIconComponent(attribute.iconName, attribute.type);
    return (
      <IconComponent
        className={cn("size-[22px] shrink-0", className)}
        stroke={1.8}
        strokeWidth={1.8}
      />
    );
  },
);

AttributeIcon.displayName = "AttributeIcon";
